import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Edit, Trash2, Briefcase, Share2, Upload, FileText, Users, Wand2, Eye, Search } from 'lucide-react';
import RecruiterLayout from '@/components/RecruiterLayout';
import CandidateDetailDialog from '@/components/CandidateDetailDialog';
import CandidateForm from '@/components/CandidateForm';
import SimpleCandidateForm from '@/components/SimpleCandidateForm';
import ShareCandidateDialog from '@/components/ShareCandidateDialog';
import BulkResumeUpload from '@/components/BulkResumeUpload';
import ParsedCandidatesView from '@/components/ParsedCandidatesView';
import ParsingStatusBadge from '@/components/ParsingStatusBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { DataTable, DataTableColumn, DataTableAction, MassAction } from '@/components/ui/data-table';
import { UniversalFilters, FilterFieldConfig } from '@/components/filters';
import { FilterValue } from '@/hooks/useSavedFilters';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BulkLushaDiscover } from '@/components/lusha';

interface Candidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_primary: string | null;
  phone_primary: string | null;
  city: string | null;
  availability_status: string | null;
  employment_status: string | null;
  desired_salary_min: number | null;
  desired_salary_max: number | null;
  desired_salary_currency: string | null;
  created_at: string;
  candidate_applications?: { jobs: { title: string } | null }[];
  candidate_skills?: { skill_id: string }[];
}

interface Skill {
  id: string;
  name: string;
  type: string;
}

const CandidateManagement = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [simpleFormOpen, setSimpleFormOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [shareCandidate, setShareCandidate] = useState<{ id: string; name: string } | null>(null);
  
  const [bulkAddToJobOpen, setBulkAddToJobOpen] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [autoLinkLoading, setAutoLinkLoading] = useState(false);
  const [pendingBulkCandidates, setPendingBulkCandidates] = useState<Candidate[]>([]);
  
  // Advanced filters - state managed by UniversalFilters, we just track the current values
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([]);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();

  useEffect(() => {
    fetchCandidates();
    fetchJobs();
    fetchSkills();
  }, []);
  
  const fetchSkills = async () => {
    const { data } = await supabase
      .from('skills')
      .select('id, name, type')
      .eq('is_bucket', false)
      .order('name');
    setSkills(data || []);
  };

  const fetchCandidates = async () => {
    try {
      const { data, error } = await supabase
        .from('candidates')
        .select(`
          *,
          candidate_applications (
            jobs (title)
          ),
          candidate_skills (
            skill_id
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCandidates(data || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    const { data } = await supabase
      .from('jobs')
      .select('id, title, status')
      .in('status', ['draft', 'published'])
      .order('created_at', { ascending: false });
    setJobs(data || []);
  };

  const handleDelete = async (candidate: Candidate) => {
    if (!confirm('Are you sure you want to delete this candidate?')) return;
    
    try {
      const { error } = await supabase.from('candidates').delete().eq('id', candidate.id);
      if (error) throw error;
      toast({ title: 'Candidate deleted successfully' });
      fetchCandidates();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleBulkDelete = async (selectedCandidates: Candidate[]) => {
    if (!confirm(`Are you sure you want to delete ${selectedCandidates.length} candidates?`)) return;
    
    setBulkLoading(true);
    try {
      const ids = selectedCandidates.map(c => c.id);
      const { error } = await supabase.from('candidates').delete().in('id', ids);
      if (error) throw error;
      toast({ title: `${selectedCandidates.length} candidates deleted successfully` });
      fetchCandidates();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAddToJob = async () => {
    if (!selectedJobId || pendingBulkCandidates.length === 0) return;
    
    setBulkLoading(true);
    try {
      const applications = pendingBulkCandidates.map(c => ({
        candidate_id: c.id,
        job_id: selectedJobId,
        status: 'new'
      }));

      const { error } = await supabase
        .from('candidate_applications')
        .upsert(applications, { onConflict: 'candidate_id,job_id', ignoreDuplicates: true });
      
      if (error) throw error;
      toast({ title: `${pendingBulkCandidates.length} candidate(s) added to job` });
      setBulkAddToJobOpen(false);
      setSelectedJobId('');
      setPendingBulkCandidates([]);
      fetchCandidates();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAutoLinkSkills = async () => {
    setAutoLinkLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('bulk-auto-link-skills');
      if (error) throw error;
      toast({ 
        title: 'Auto-link complete', 
        description: `Processed ${data.processed}/${data.total} candidates. ${data.failed} failed.` 
      });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setAutoLinkLoading(false);
    }
  };

  const handleEdit = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setSimpleFormOpen(true);
  };

  const handleAdvancedCreate = () => {
    setSelectedCandidate(null);
    setFormOpen(true);
  };

  const handleShare = (candidate: Candidate) => {
    setShareCandidate({
      id: candidate.id,
      name: `${candidate.first_name} ${candidate.last_name}`,
    });
    setShareOpen(true);
  };

  const handleView = (candidate: Candidate) => {
    setSelectedCandidateId(candidate.id);
    setDetailOpen(true);
  };

  const openBulkAddToJob = (selectedCandidates: Candidate[]) => {
    setPendingBulkCandidates(selectedCandidates);
    setBulkAddToJobOpen(true);
  };

  // Filter field configs for UniversalFilters
  const filterFields: FilterFieldConfig[] = useMemo(() => [
    {
      field: 'availability_status',
      label: 'Availability',
      type: 'select',
      options: [
        { label: 'Available', value: 'available' },
        { label: 'Open to Offers', value: 'open' },
        { label: 'Not Looking', value: 'not_looking' },
      ],
    },
    {
      field: 'employment_status',
      label: 'Employment',
      type: 'select',
      options: [
        { label: 'Employed', value: 'employed' },
        { label: 'Unemployed', value: 'unemployed' },
        { label: 'Freelance', value: 'freelance' },
      ],
    },
    {
      field: 'city',
      label: 'City',
      type: 'text',
    },
    {
      field: 'skills',
      label: 'Skills',
      type: 'multi-select',
      options: skills.map(s => ({ label: s.name, value: s.id })),
    },
  ], [skills]);

  // Apply advanced filters
  const filteredCandidates = useMemo(() => {
    if (activeFilters.length === 0) return candidates;
    
    return candidates.filter(candidate => {
      return activeFilters.every(filter => {
        // Skills filter with AND/OR logic
        if (filter.field === 'skills') {
          const candidateSkillIds = candidate.candidate_skills?.map(cs => cs.skill_id) || [];
          const filterSkillIds = Array.isArray(filter.value) ? filter.value : [filter.value];
          
          if (filterSkillIds.length === 0) return true;
          
          if (filter.logic === 'AND') {
            return filterSkillIds.every(skillId => candidateSkillIds.includes(skillId));
          } else {
            return filterSkillIds.some(skillId => candidateSkillIds.includes(skillId));
          }
        }
        
        // Standard filters
        const candidateValue = (candidate as any)[filter.field];
        
        switch (filter.operator) {
          case 'eq':
            return candidateValue === filter.value;
          case 'neq':
            return candidateValue !== filter.value;
          case 'contains':
            return String(candidateValue || '').toLowerCase().includes(String(filter.value).toLowerCase());
          case 'in':
            return Array.isArray(filter.value) ? filter.value.includes(candidateValue) : candidateValue === filter.value;
          case 'gt':
            return candidateValue > filter.value;
          case 'lt':
            return candidateValue < filter.value;
          case 'gte':
            return candidateValue >= filter.value;
          case 'lte':
            return candidateValue <= filter.value;
          default:
            return true;
        }
      });
    });
  }, [candidates, activeFilters]);

  const columns: DataTableColumn<Candidate>[] = [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span>{`${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}</span>
          <ParsingStatusBadge candidateId={row.id} />
        </div>
      ),
      sortable: true,
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email_primary',
      cell: (row) => row.email_primary || '-',
      sortable: true,
    },
    {
      id: 'phone',
      header: 'Phone',
      accessorKey: 'phone_primary',
      cell: (row) => row.phone_primary || '-',
      sortable: true,
    },
    {
      id: 'city',
      header: 'City',
      accessorKey: 'city',
      cell: (row) => row.city || '-',
      sortable: true,
    },
    {
      id: 'availability',
      header: 'Availability',
      accessorKey: 'availability_status',
      cell: (row) => {
        const status = row.availability_status || 'unknown';
        const colors: Record<string, string> = {
          available: 'bg-green-500/10 text-green-700',
          open: 'bg-blue-500/10 text-blue-700',
          not_looking: 'bg-gray-500/10 text-gray-700',
        };
        return (
          <Badge className={colors[status] || colors.not_looking}>
            {status.replace('_', ' ')}
          </Badge>
        );
      },
      sortable: true,
    },
    {
      id: 'employment',
      header: 'Employment',
      accessorKey: 'employment_status',
      cell: (row) => (
        <Badge variant="outline">{row.employment_status?.replace('_', ' ') || '-'}</Badge>
      ),
      sortable: true,
      defaultVisible: false,
    },
    {
      id: 'salary',
      header: 'Salary',
      accessorKey: 'desired_salary_min',
      cell: (row) => {
        if (!row.desired_salary_min) return '-';
        return (
          <span className="text-sm">
            {row.desired_salary_min.toLocaleString()}
            {row.desired_salary_max && ` - ${row.desired_salary_max.toLocaleString()}`}
            {row.desired_salary_currency && ` ${row.desired_salary_currency}`}
          </span>
        );
      },
      sortable: true,
      defaultVisible: false,
    },
    {
      id: 'skills_count',
      header: 'Skills',
      accessorFn: (row) => row.candidate_skills?.length || 0,
      cell: (row) => (
        <Badge variant="outline">{row.candidate_skills?.length || 0}</Badge>
      ),
      sortable: true,
      defaultVisible: false,
    },
    {
      id: 'jobs',
      header: 'Applied Jobs',
      accessorFn: (row) => row.candidate_applications?.length || 0,
      cell: (row) => {
        const count = row.candidate_applications?.length || 0;
        if (count === 0) return '-';
        return (
          <Badge variant="outline">
            <Briefcase className="h-3 w-3 mr-1" />
            {count}
          </Badge>
        );
      },
      sortable: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      cell: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: true,
      defaultVisible: false,
    },
  ];

  const actions: DataTableAction<Candidate>[] = [
    {
      label: 'View Details',
      icon: <Eye className="h-4 w-4" />,
      onClick: handleView,
    },
    {
      label: 'Applications',
      icon: <Briefcase className="h-4 w-4" />,
      onClick: (c) => navigate(`/recruiter/candidates/${c.id}/applications`),
      show: (c) => (c.candidate_applications?.length || 0) > 0,
    },
    {
      label: 'Share Profile',
      icon: <Share2 className="h-4 w-4" />,
      onClick: handleShare,
    },
    {
      label: 'Edit',
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
      show: () => can.update('candidates'),
    },
    {
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      variant: 'destructive',
      show: () => can.delete('candidates'),
    },
  ];

  const massActions: MassAction<Candidate>[] = [
    {
      label: 'Add to Job',
      icon: <Briefcase className="h-4 w-4 mr-2" />,
      onClick: openBulkAddToJob,
    },
    {
      label: 'Delete Selected',
      icon: <Trash2 className="h-4 w-4 mr-2" />,
      onClick: handleBulkDelete,
      variant: 'destructive',
    },
  ];

  return (
    <RecruiterLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Candidate Management</h1>
            <p className="text-muted-foreground">View and manage candidate applications</p>
          </div>
          <div className="flex gap-3">
            {can.create('candidates') && (
              <Button variant="outline" onClick={() => setBulkUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Bulk Upload
              </Button>
            )}
            {can.update('candidates') && (
              <Button variant="outline" onClick={handleBulkAutoLinkSkills} disabled={autoLinkLoading}>
                {autoLinkLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                Auto-Link Skills
              </Button>
            )}
            {can.create('candidates') && (
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Candidate
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All Candidates</TabsTrigger>
            <TabsTrigger value="parsed">
              <FileText className="h-4 w-4 mr-1" />
              Recently Parsed
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-4 space-y-4">
            <UniversalFilters
              entityType="candidates"
              fields={filterFields}
              onFiltersChange={setActiveFilters}
            />
            <DataTable
              data={filteredCandidates}
              columns={columns}
              keyField="id"
              searchPlaceholder="Search candidates..."
              searchFields={['first_name', 'last_name', 'email_primary', 'city']}
              actions={actions}
              massActions={can.delete('candidates') || can.update('candidates') ? massActions : []}
              onRowClick={handleView}
              titleField="name"
              subtitleField="email"
              emptyMessage="No candidates found"
              emptyIcon={<Users className="h-12 w-12" />}
              loading={loading}
            />
          </TabsContent>
          
          <TabsContent value="parsed" className="mt-4">
            <ParsedCandidatesView />
          </TabsContent>
        </Tabs>

        <CandidateDetailDialog
          candidateId={selectedCandidateId}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />

        <CandidateForm
          candidate={selectedCandidate}
          open={formOpen}
          onOpenChange={setFormOpen}
          onSuccess={() => {
            fetchCandidates();
            setFormOpen(false);
          }}
        />

        <ShareCandidateDialog
          candidateId={shareCandidate?.id || ''}
          candidateName={shareCandidate?.name || ''}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />

        <BulkResumeUpload
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          onSuccess={fetchCandidates}
        />

        {/* Bulk Add to Job Dialog */}
        <Dialog open={bulkAddToJobOpen} onOpenChange={setBulkAddToJobOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Candidates to Job</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select a job to add {pendingBulkCandidates.length} candidate(s) to:
              </p>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map(job => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title} ({job.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkAddToJobOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkAddToJob} disabled={!selectedJobId || bulkLoading}>
                {bulkLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add to Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Simple Add Candidate Form (resume-first) */}
        <SimpleCandidateForm
          open={simpleFormOpen}
          onOpenChange={setSimpleFormOpen}
          onSuccess={fetchCandidates}
        />
      </div>
    </RecruiterLayout>
  );
};

export default CandidateManagement;
